import * as React from 'react';

import { ProductCard } from '@coursera/cds-core';

import Prerequisites from 'bundles/degree-home/components/v3-dpm/components/CourseCard/Prerequisites';
import UnknownAvailabilityDescription from 'bundles/degree-home/components/v3-dpm/components/CourseCard/UnknownAvailabilityDescription';
import { useLearnerPlanStoreContext } from 'bundles/degree-home/components/v3-dpm/context/LearnerPlanStoreContext';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as SegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import { checkCourseHasUnknownAvailability } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import CreditCount from 'bundles/program-loggedin-shared/components/CreditCount';
import { METADATA_SEPARATOR } from 'bundles/program-loggedin-shared/components/Metadata';
import RequirementType from 'bundles/program-loggedin-shared/components/RequirementType';

import DefaultCourseImg from 'images/degree-home/default-course-logo.png';

import _t from 'i18n!nls/degree-home';

interface PlanCourseCardProps {
  course: SegmentCourse;
}

// Component for displaying a course card in the modals used to display a course in the Degree Planning tool.
export const PlanCourseCard = ({ course }: PlanCourseCardProps) => {
  const { segmentCourse } = course;
  const { requirementTypeData } = useLearnerPlanStoreContext();

  const courseHasUnknownAvailability = checkCourseHasUnknownAvailability(segmentCourse);

  // fail gracefully if requirementTypeData is undefined
  const isElective = requirementTypeData?.[course.segmentCourse.id]?.isElective;

  const { courseraCourse, courseCode } = segmentCourse.course;

  const courseName = segmentCourse.course.courseName;

  return (
    <ProductCard
      style={{ boxShadow: 'none', transform: 'none' }}
      variant="list"
      productType="Course"
      previewImageSrc={courseraCourse?.promoPhotoUrl || DefaultCourseImg}
      title={{
        name: _t('#{courseCode} - #{courseName}', {
          courseName,
          courseCode,
        }),
      }}
      body={
        <React.Fragment>
          <Prerequisites prerequisite={segmentCourse.prerequisite} />
          {isElective !== undefined && (
            <React.Fragment>
              <RequirementType isElective={isElective} key="RequirementType" />
              {METADATA_SEPARATOR}
            </React.Fragment>
          )}
          <CreditCount credits={segmentCourse.creditValue} />
          {courseHasUnknownAvailability && <UnknownAvailabilityDescription />}
        </React.Fragment>
      }
    />
  );
};

export default PlanCourseCard;
